/* eslint-disable object-curly-newline */
/* eslint-disable no-console */
import { Theme } from "@emotion/react";
import { createTheme, ThemeOptions } from "@mui/material/styles";

import { isMobile, isDesktop, isTablet, isAndroid, isIOS } from "react-device-detect";

/**
 * PC/モバイル判定用のフック
 * ※モバイル=スマホ/タブレット(iOS/Androidのみ対応)
 * @returns object { desktop: boolean, mobile: boolean }
 */
export const useScreenSize = (): { desktop: boolean; mobile: boolean } => {
    const mobile = isMobile || isTablet || isAndroid || isIOS;
    const desktop = !mobile || isDesktop;
    return {
        mobile,
        desktop,
    };
};

export const ZINDEX_BASE: number = 1;
export const ZINDEX_HEADER: number = 2000;
export const ZINDEX_MENU: number = 2500;
export const ZINDEX_DRAWER: number = 3000;
export const ZINDEX_MODAL: number = 5000;
export const ZINDEX_FOOTER: number = 6000;
export const ZINDEX_MESSAGE: number = 7000;

const FONT_SIZE_DEFAULT = 16;

const BASE_THEME: Partial<ThemeOptions> = {
    palette: {
        primary: {
            main: "#CC0022",
            contrastText: "#444c55",
        },
        secondary: {
            main: "#29267A",
        },
    },
    typography: {
        fontFamily: ["Noto Sans JP", "sans-serif"].join(","),
        fontSize: FONT_SIZE_DEFAULT,
        h1: {
            fontSize: "1.802em",
            letterSpacing: "0.05em",
            fontWeight: "bold",
            "@media (max-width: 899px)": {
                fontSize: "1.702em",
            },
            "@media (max-width: 599px)": {
                fontSize: "1.25em",
            },
        },
        h2: {
            fontSize: "1.602em",
            letterSpacing: "0.05em",
            fontWeight: "bold",
            "@media (max-width: 899px)": {
                fontSize: "1.502em",
            },
            "@media (max-width: 599px)": {
                fontSize: "1.166em",
            },
        },
        h3: {
            fontSize: "1.424em",
            letterSpacing: "0.05em",
            fontWeight: "bold",
            "@media (max-width: 899px)": {
                fontSize: "1.324em",
            },
            "@media (max-width: 599px)": {
                fontSize: "1.083em",
            },
        },
        h4: {
            fontSize: "1.266em",
            letterSpacing: "0.05em",
            fontWeight: "bold",
            "@media (max-width: 899px)": {
                fontSize: "1.166em",
            },
            "@media (max-width: 599px)": {
                fontSize: "1em",
            },
        },
        h5: {
            fontSize: "1.125em",
            letterSpacing: "0.05em",
            fontWeight: "bold",
            "@media (max-width: 899px)": {
                fontSize: "1.025em",
            },
            "@media (max-width: 599px)": {
                fontSize: "0.916em",
            },
        },
        h6: {
            fontSize: "1em",
            letterSpacing: "0.05em",
            "@media (max-width: 899px)": {
                fontSize: "0.95em",
            },
            "@media (max-width: 599px)": {
                fontSize: "0.833em",
            },
        },
        body1: {
            fontSize: "0.889em",
            letterSpacing: "0.05em",
            "@media (max-width: 899px)": {
                fontSize: "0.889em",
            },
            "@media (max-width: 599px)": {
                fontSize: "0.81em",
            },
        },
        body2: {
            fontSize: "0.79em",
            letterSpacing: "0.05em",
            "@media (max-width: 899px)": {
                fontSize: "0.79em",
            },
            "@media (max-width: 599px)": {
                fontSize: "0.79em",
            },
        },
        button: {
            textTransform: "none",
        },
    },
};

// CssBaseline(リセットCSS)：デフォ設定
const STYLE_OVERRIDES_CSS_BASELINE: Omit<Theme, "components"> = {
    body: {
        "font-size": `${FONT_SIZE_DEFAULT}px`,
    },
};

// CssBaseline(リセットCSS)：PC版 ⇒ スクロールバーのスタイルを上書き
const STYLE_OVERRIDES_CSS_BASELINE_DESKTOP: Omit<Theme, "components"> = {
    "::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
    },
    "::-webkit-scrollbar-track": {
        "border-radius": "5px",
    },
    "::-webkit-scrollbar-thumb": {
        "background-color": "#e0e0e0",
        "border-radius": "5px",
    },
};

const createComponentThemeOption = (): Partial<ThemeOptions> => {
    const { desktop } = useScreenSize();
    // console.log("createComponentThemeOption: desktop", desktop);

    const components = {
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    ...STYLE_OVERRIDES_CSS_BASELINE,
                    ...(desktop ? STYLE_OVERRIDES_CSS_BASELINE_DESKTOP : {}),
                },
            },
        },
    };
    // console.log("createComponentThemeOption: components", components);

    return components;
};

const SOMPO_THEME = createTheme({ ...BASE_THEME, ...createComponentThemeOption() });

export default SOMPO_THEME;
